import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Sparkles, Send, ArrowRight, FileText, LifeBuoy, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { pageContext } from "@/lib/activity";
import { TicketDialog } from "@/components/agyin/RaiseTicketButton";

function useScrolling() {
  const [on, setOn] = useState(false);
  useEffect(() => {
    let t;
    const onScroll = () => { setOn(true); clearTimeout(t); t = setTimeout(() => setOn(false), 900); };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => { window.removeEventListener("scroll", onScroll); clearTimeout(t); };
  }, []);
  return on;
}

import { StatusBadge } from "@/components/common";
import { Markdown } from "@/components/agyin/Markdown";
import { ProCard, Insight } from "@/components/agyin/ProCards";
import { ProInsights, UsageNotice, DailyBrief, PinnedReview } from "@/components/agyin/ProExtras";
import { toast } from "sonner";

const SUGGEST = ["What is happening today?", "Show overdue invoices", "Show all quotes awaiting approval", "Paste a job brief to draft a quote"];
const visible = (t) => t.replace(/```agyin[\s\S]*$/, "").trim();
const newSid = () => `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
const AREAS = { "": "the dashboard", jobs: "Jobs", quotes: "Quotations", invoices: "Invoices", payments: "Payments", customers: "Customers", crm: "CRM",
  inventory: "Inventory", procurement: "Procurement", maintenance: "Maintenance", accounting: "Accounting", hr: "HR", payroll: "Payroll", reports: "Reports",
  tickets: "Support tickets", users: "Users & roles", settings: "Settings", templates: "Templates", branches: "Branches", billing: "Billing" };
const areaName = (path) => { const [, , seg = "", id] = path.split("/"); const a = AREAS[seg] || seg.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase()); return id && id !== "new" && seg ? `${a}, on a record page` : id === "new" ? `${a}, creating a new record` : a; };

export async function stream(body, onDelta, signal, path = "/api/agyin/chat") {
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
    method: "POST", signal, headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("pressops_token")}` }, body: JSON.stringify(body) });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || "Agyin is unavailable");
  const reader = r.body.getReader();
  const dec = new TextDecoder();
  let buf = "", done = null;
  for (;;) {
    const { value, done: end } = await reader.read();
    if (end) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const ev = JSON.parse(p.replace(/^data: /, ""));
      if (ev.t === "delta") onDelta(ev.c);
      if (ev.t === "error") throw new Error(ev.c);
      if (ev.t === "done") done = ev;
    }
  }
  return done;
}

function ActionCard({ m, onTicket, onClose }) {
  const navigate = useNavigate();
  const a = m.action;
  if (!a) return null;
  if (a.action === "navigate") return (
    <Button size="sm" data-testid="agyin-navigate-button" className="mt-2 bg-[#13294B] hover:bg-[#1c3a66]"
      onClick={async () => { onClose(); await api.post("/agyin/log", { action: "navigate_executed", route: a.route }).catch(() => {}); navigate(a.route); }}>
      {a.label} <ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>);
  if (a.action === "quote_form") return (
    <Button size="sm" data-testid="agyin-quote-form-button" className="mt-2 bg-[#13294B] hover:bg-[#1c3a66]" onClick={() => {
      sessionStorage.setItem("agyin_quote_form", JSON.stringify({ service_line: a.service_line, job_type: a.job_type }));
      onClose(); navigate(`/app/quotes/new?jt=${a.job_type}`);
    }}>{a.label} <ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>);
  if (a.action === "open_finishing") return (
    <Button size="sm" data-testid="agyin-open-finishing-button" className="mt-2 bg-[#13294B] hover:bg-[#1c3a66]" onClick={() => {
      onClose();
      if (!/\/app\/quotes\/(new|[^/]+\/revise)/.test(window.location.pathname)) navigate("/app/quotes/new");
      setTimeout(() => window.dispatchEvent(new CustomEvent("agyin:add-finishing")), 300);
    }}>Add finishing <ArrowRight className="ml-1 h-3.5 w-3.5" /></Button>);
  if (a.action === "quote_draft") {
    const d = a.draft || {};
    return (
      <div className="mt-2 rounded-lg border p-3 text-xs" style={{ borderColor: "var(--po-border)" }} data-testid="agyin-quote-draft">
        <div className="mb-1 flex items-center gap-1.5 font-semibold text-slate-900"><FileText className="h-3.5 w-3.5" />Draft quote · nothing saved yet</div>
        <div>Customer: <b>{d.customer_name || "—"}</b>{!d.customer_id && d.customer_name && <StatusBadge tone="warning" label="no match" className="ml-1" />}</div>
        <div>Title: {d.title || "—"}</div>
        <ul className="mt-1 list-disc pl-4">{(d.lines || []).map((l, i) => <li key={i}>{l.service_line} · {l.description} · qty {l.quantity}{l.width_mm ? ` · ${l.width_mm}×${l.length_mm} mm` : ""}</li>)}</ul>
        <Button size="sm" data-testid="agyin-quote-review-button" className="mt-2 bg-[#45BFB4] text-[#13294B] hover:bg-[#389F95] hover:text-white" onClick={async () => {
          sessionStorage.setItem("agyin_quote_draft", JSON.stringify(d));
          onClose();
          await api.post("/agyin/log", { action: "quote_draft_opened", detail: d }).catch(() => {});
          navigate("/app/quotes/new");
        }}>Review &amp; confirm in quote builder</Button>
      </div>);
  }
  if (a.action === "offer_ticket") return (
    <div className="mt-2 flex gap-2"><Button size="sm" data-testid="agyin-create-ticket-button" onClick={() => onTicket(a)} className="bg-[#13294B] hover:bg-[#1c3a66]"><LifeBuoy className="mr-1 h-3.5 w-3.5" />Yes, create a ticket</Button>
      <Button size="sm" variant="outline" data-testid="agyin-decline-ticket-button" onClick={() => api.post("/agyin/log", { action: "ticket_offer_declined" }).catch(() => {})}>No thanks</Button></div>);
  return <ProCard a={a} onClose={onClose} />;
}

function Drafts({ drafts, reload }) {
  if (!drafts?.length) return null;
  const act = async (d, ok) => { try { ok ? await api.post(`/tickets/${d.id}/submit`) : await api.delete(`/tickets/${d.id}`); toast.success(ok ? "Ticket submitted" : "Draft discarded"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-2 border-b px-4 py-3" style={{ borderColor: "var(--po-border)" }} data-testid="agyin-anomaly-drafts">
      <div className="po-label">Agyin detected issues · confirm to open a ticket</div>
      {drafts.map((d) => <div key={d.id} className="rounded-lg border p-2 text-xs po-tone-warning" data-testid={`anomaly-draft-${d.id}`}>
        <div className="font-semibold">{d.summary}</div><div>{d.description}</div>
        <div className="mt-1.5 flex gap-2"><Button size="sm" data-testid={`anomaly-submit-${d.id}`} onClick={() => act(d, true)}>Submit ticket</Button><Button size="sm" variant="outline" onClick={() => act(d, false)}>Discard</Button></div></div>)}
    </div>
  );
}

export function AgyinPanel() {
  const { user } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const pro = user?.tenant?.capabilities?.includes("ai_pro");
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState(() => sessionStorage.getItem("agyin_sid") || newSid());
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [usage, setUsage] = useState(null);
  const [brief, setBrief] = useState(null);
  const [review, setReview] = useState(null);
  const end = useRef(null);
  const ctrl = useRef(null);
  useEffect(() => { sessionStorage.setItem("agyin_sid", sid); }, [sid]);
  useEffect(() => { if (open && !msgs.length) api.get(`/agyin/sessions/${sid}`).then((r) => setMsgs(r.data)).catch(() => {}); }, [open, sid]); // eslint-disable-line react-hooks/exhaustive-deps
  const scan = () => pro && api.post("/agyin/anomalies/scan").then((r) => setDrafts(r.data.drafts || [])).catch(() => {});
  useEffect(() => { if (open && user?.role === "tenant_admin") scan(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    api.get("/agyin/brief").then((r) => setBrief(r.data)).catch(() => {});
    if (pro) api.get("/agyin/usage").then((r) => setUsage(r.data)).catch(() => {});
    if (user?.role === "tenant_admin") api.get("/agyin/reviews/latest").then((r) => setReview(r.data.review)).catch(() => {});
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setOpen(new URLSearchParams(loc.search).get("agyin") === "review"); }, [loc.pathname, loc.search]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  useEffect(() => () => ctrl.current?.abort(), []);
  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    ctrl.current?.abort();
    ctrl.current = new AbortController();
    try {
      const done = await stream({ session_id: sid, message, context: pageContext(loc.pathname) }, (c) => setMsgs((m) => { const n = [...m]; n[n.length - 1] = { ...n[n.length - 1], content: n[n.length - 1].content + c }; return n; }), ctrl.current.signal);
      setMsgs((m) => { const n = [...m]; n[n.length - 1] = { role: "assistant", content: done?.text ?? visible(n[n.length - 1].content), action: done?.action }; return n; });
      if (done?.action?.usage) setUsage(done.action.usage);
      if (done?.action?.action === "open_finishing") window.dispatchEvent(new CustomEvent("agyin:add-finishing"));
    } catch (e) {
      if (e.name === "AbortError") return;
      setMsgs((m) => { const n = [...m]; n[n.length - 1] = { role: "assistant", content: e.message, error: true }; return n; });
    } finally { setBusy(false); }
  };
  const scrolling = useScrolling();
  return (
    <>
      <button data-testid="agyin-help-button" onClick={() => setOpen(true)} aria-label="Help and support — open Agyin"
        className={`fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-[#13294B] px-4 py-2.5 text-sm font-medium text-white shadow-[var(--po-shadow-raise)] transition-[transform,opacity] duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#45BFB4] print:hidden ${scrolling ? "max-sm:pointer-events-none max-sm:translate-y-16 max-sm:opacity-0" : ""}`}>
        <Sparkles className="h-4 w-4 text-[#45BFB4]" /><span className="hidden sm:inline">Help · Agyin</span></button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:w-[420px] sm:max-w-none lg:w-[33vw] lg:min-w-[400px] lg:max-w-[560px]" data-testid="agyin-panel">
          <SheetHeader className="border-b px-4 py-3 text-left" style={{ borderColor: "var(--po-border)" }}>
            <SheetTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#2B7F77]" />Agyin</SheetTitle>
            <SheetDescription className="text-xs" data-testid="agyin-page-context">You're in {areaName(loc.pathname)}. Ask me anything or pick a suggestion below.</SheetDescription>
          </SheetHeader>
          <Drafts drafts={drafts} reload={scan} />
          {pro && <UsageNotice usage={usage} />}
          <PinnedReview review={review} />
          <ProInsights pro={pro} onNavigate={() => setOpen(false)} onUsage={setUsage} renderItems={(items) => <Insight a={{ items }} onClose={() => setOpen(false)} />} />
          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="agyin-messages">
            {!msgs.length && <div className="space-y-2"><p className="text-sm text-slate-600">Hi {user?.name?.split(" ")[0]}, how can I help?</p>
              <DailyBrief data={brief} onNavigate={() => setOpen(false)} />
              {SUGGEST.filter((s) => pro || !s.startsWith("Paste")).map((s) => <button key={s} onClick={() => (s.startsWith("Paste") ? setText("Draft a quote from this: ") : send(s))} data-testid={`agyin-suggest-${s.slice(0, 10).replace(/\W/g, "")}`} className="block w-full rounded-lg border px-3 py-2 text-left text-xs text-slate-700 transition-colors hover:bg-slate-50" style={{ borderColor: "var(--po-border)" }}>{s}</button>)}
              {!pro && <div data-testid="agyin-pro-upsell" className="flex items-start gap-2 rounded-lg border border-dashed px-3 py-2 text-xs text-slate-600" style={{ borderColor: "var(--po-border)" }}>
                <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#2B7F77]" />
                <span>Draft quotes from a pasted job brief with Agyin Pro. {user?.role === "tenant_admin"
                  ? <button data-testid="agyin-pro-upgrade-link" onClick={() => { setOpen(false); navigate("/app/billing"); }} className="font-semibold text-[#13294B] underline">Add it from Billing</button>
                  : "Ask your workspace admin to add it from Billing."}</span></div>}</div>}
            {msgs.map((m, i) => (
              <div key={i} className={m.role === "user" ? "ml-8 rounded-lg bg-[#13294B] px-3 py-2 text-sm text-white" : `mr-4 rounded-lg border px-3 py-2 text-sm ${m.error ? "po-tone-danger" : "bg-white text-slate-800"}`} style={m.role === "user" ? undefined : { borderColor: "var(--po-border)" }} data-testid={`agyin-msg-${m.role}`}>
                {m.role === "user" ? <div className="whitespace-pre-wrap">{m.content}</div>
                  : visible(m.content) ? <Markdown text={visible(m.content)} /> : <div>{busy && i === msgs.length - 1 ? "Thinking…" : ""}</div>}
                {m.role === "assistant" && <ActionCard m={m} onTicket={setTicket} onClose={() => setOpen(false)} />}
              </div>))}
            <div ref={end} />
          </div>
          <div className="border-t p-3" style={{ borderColor: "var(--po-border)" }}>
            <div className="flex items-end gap-2">
              <Textarea data-testid="agyin-input" rows={2} value={text} onChange={(e) => setText(e.target.value)} placeholder={pro ? "Ask Agyin, or paste a job brief…" : "Ask Agyin…"}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} className="min-h-[44px] resize-none" />
              <Button data-testid="agyin-send-button" disabled={busy || !text.trim()} onClick={() => send()} className="bg-[#13294B] hover:bg-[#1c3a66]"><Send className="h-4 w-4" /></Button>
            </div>
            <div className="mt-2 flex justify-between text-[11px] text-slate-400">
              <button data-testid="agyin-new-chat" onClick={() => { setSid(newSid()); setMsgs([]); }} className="inline-flex items-center gap-1 hover:text-slate-600"><X className="h-3 w-3" />New chat</button>
              <button data-testid="agyin-freeform-ticket" onClick={() => setTicket({})} className="hover:text-slate-600">Raise a support ticket</button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      {ticket && <TicketDialog open initial={ticket} onClose={() => setTicket(null)} />}
    </>
  );
}
